
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Link2, CheckCircle, AlertCircle, Loader2, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { ExternalAccountLink as ExternalAccountLinkType } from "@/hooks/useCourseStats";
import { format } from "date-fns";
import { fr, enUS, es } from "date-fns/locale";

interface ExternalAccountLinkProps {
  accountLink: ExternalAccountLinkType | null;
  isLoading: boolean;
  onLinkSuccess: () => void;
}

const ExternalAccountLink = ({ accountLink, isLoading, onLinkSuccess }: ExternalAccountLinkProps) => {
  const { t, i18n } = useTranslation('course-stats');
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLinking, setIsLinking] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);

  const getLocale = () => {
    switch (i18n.language) {
      case 'fr': return fr;
      case 'es': return es;
      default: return enUS;
    }
  };

  const handleLinkAccount = async () => {
    if (!email.trim() || !password.trim()) {
      toast({
        variant: "destructive",
        title: t('accountLink.error'),
        description: t('accountLink.credentialsRequired'),
      });
      return;
    }

    try {
      setIsLinking(true);
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error("Not authenticated");
      }

      // First, verify credentials with external project
      const { data, error: verifyError } = await supabase.functions.invoke('verify-external-account', {
        body: { email: email.trim(), password: password.trim() }
      });

      if (verifyError) {
        throw new Error(verifyError.message || t('accountLink.verificationFailed'));
      }

      if (data?.error) {
        throw new Error(data.error === 'Invalid credentials' ? t('accountLink.invalidCredentials') : data.error);
      }

      // If verification successful, insert the account link (already verified)
      const { error: insertError } = await supabase
        .from("external_account_links")
        .upsert({
          user_id: user.id,
          external_user_email: email.trim(),
          external_user_id: data.external_user_id,
          is_verified: true,
        }, { onConflict: 'user_id' });

      if (insertError) {
        throw insertError;
      }

      toast({
        title: t('accountLink.success'),
        description: t('accountLink.verifiedDescription'),
      });

      setEmail("");
      setPassword("");
      onLinkSuccess();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: t('accountLink.error'),
        description: error.message,
      });
    } finally {
      setIsLinking(false);
    }
  };

  const handleVerifyAccount = async () => {
    if (!password.trim()) {
      toast({
        variant: "destructive",
        title: t('accountLink.error'),
        description: t('accountLink.passwordRequired'),
      });
      return;
    }

    try {
      setIsVerifying(true);
      
      const { data, error } = await supabase.functions.invoke('verify-external-account', {
        body: { email: accountLink?.external_user_email, password: password.trim() }
      });

      if (error) {
        throw new Error(error.message || t('accountLink.verificationFailed'));
      }

      if (data?.error) {
        throw new Error(data.error === 'Invalid credentials' ? t('accountLink.invalidCredentials') : data.error);
      }

      toast({
        title: t('accountLink.verified'),
        description: t('accountLink.verifiedDescription'),
      });

      setPassword("");
      onLinkSuccess();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: t('accountLink.error'),
        description: error.message,
      });
    } finally {
      setIsVerifying(false);
    }
  };

  const handleUnlinkAccount = async () => {
    if (!accountLink) return;

    try {
      setIsDeleting(true);

      // Delete stats first
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase
          .from("external_round_stats")
          .delete()
          .eq("user_id", user.id);
      }

      // Delete account link
      const { error } = await supabase
        .from("external_account_links")
        .delete()
        .eq("id", accountLink.id);

      if (error) {
        throw error;
      }

      toast({
        title: t('accountLink.unlinked'),
        description: t('accountLink.unlinkedDescription'),
      });

      onLinkSuccess();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: t('accountLink.error'),
        description: error.message,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <Card className="mb-6">
        <CardContent className="py-8 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-golf-600" />
        </CardContent>
      </Card>
    );
  }

  if (accountLink) {
    return (
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Link2 className="h-5 w-5 text-golf-600" />
              <CardTitle className="text-lg">{t('accountLink.title')}</CardTitle>
            </div>
            <Badge variant={accountLink.is_verified ? "default" : "secondary"}>
              {accountLink.is_verified ? (
                <><CheckCircle className="h-3 w-3 mr-1" />{t('accountLink.verified')}</>
              ) : (
                <><AlertCircle className="h-3 w-3 mr-1" />{t('accountLink.pending')}</>
              )}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm">
              <span className="text-muted-foreground">{t('accountLink.linkedEmail')}:</span>{" "}
              <span className="font-medium">{accountLink.external_user_email}</span>
            </p>
            
            {/* Password verification section for unverified accounts */}
            {!accountLink.is_verified && (
              <div className="p-4 bg-muted/50 rounded-lg space-y-3">
                <p className="text-sm text-muted-foreground">{t('accountLink.verifyDescription')}</p>
                <div className="flex gap-4 items-end">
                  <div className="flex-1">
                    <Label htmlFor="verify-password">{t('accountLink.passwordLabel')}</Label>
                    <Input
                      id="verify-password"
                      type="password"
                      placeholder={t('accountLink.passwordPlaceholder')}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                  </div>
                  <Button onClick={handleVerifyAccount} disabled={isVerifying}>
                    {isVerifying ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <CheckCircle className="h-4 w-4 mr-2" />
                    )}
                    {t('accountLink.verifyButton')}
                  </Button>
                </div>
              </div>
            )}

            {accountLink.last_sync_at && (
              <p className="text-sm">
                <span className="text-muted-foreground">{t('accountLink.lastSync')}:</span>{" "}
                <span className="font-medium">
                  {format(new Date(accountLink.last_sync_at), "PPpp", { locale: getLocale() })}
                </span>
              </p>
            )}
            <div className="pt-2">
              <Button 
                variant="destructive" 
                size="sm" 
                onClick={handleUnlinkAccount}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4 mr-2" />
                )}
                {t('accountLink.unlink')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Link2 className="h-5 w-5 text-golf-600" />
          {t('accountLink.title')}
        </CardTitle>
        <CardDescription>{t('accountLink.description')}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="external-email">{t('accountLink.emailLabel')}</Label>
              <Input
                id="external-email"
                type="email"
                placeholder={t('accountLink.emailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="external-password">{t('accountLink.passwordLabel')}</Label>
              <Input
                id="external-password"
                type="password"
                placeholder={t('accountLink.passwordPlaceholder')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          </div>
          <Button onClick={handleLinkAccount} disabled={isLinking} className="w-full sm:w-auto">
            {isLinking ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Link2 className="h-4 w-4 mr-2" />
            )}
            {t('accountLink.linkButton')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ExternalAccountLink;
